import React, { useEffect, useRef, useState } from 'react';
import { arrayOf, bool, func, node, object, shape, string } from 'prop-types';
import classNames from 'classnames';

import Slider from 'react-slick';
import Field, { hasDataInFields } from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import { TalentCard } from '../../../../components';
import SectionContainer from '../SectionContainer';
import css from './SectionArticle.module.css';
const LANDINGPAGE_SPOTLIGHT_NUMBER = 6;

function SampleNextArrow(props) {
  const { className, style, onClick } = props;
  return (<div
    className={className}
    style={{ ...style, display: "block" }}
    onClick={onClick}
  >
    <svg width="21" height="39" viewBox="0 0 21 39" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 36.7707L19 19.8553L2 2.93994" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>

  </div>);
}

function SamplePrevArrow(props) {
  const { className, style, onClick } = props;
  return (<div
    className={className}
    style={{ ...style, display: "block" }}
    onClick={onClick}
  >
    <svg style={{ transform: 'rotate(180deg)' }} width="21" height="39" viewBox="0 0 21 39" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 36.7707L19 19.8553L2 2.93994" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>


  </div >);
}


// Skeleton card matching TalentCard structure: image left + text right
const TalentCardSkeleton = () => (
  <div className={css.skeletonCard}>
    <div className={css.skeletonBorderRadius}>
      <div className={css.skeletonContent}>
        <div className={css.skeletonImg} />
        <div className={css.skeletonRight}>
          <div className={css.skeletonLine} style={{ width: '70%', height: '16px' }} />
          <div className={css.skeletonLine} style={{ width: '55%', height: '14px' }} />
          <div className={css.skeletonLine} style={{ width: '80%', height: '12px' }} />
          <div className={css.skeletonLine} style={{ width: '60%', height: '12px' }} />
          <div className={css.skeletonLine} style={{ width: '65%', height: '12px' }} />
        </div>
      </div>
    </div>
    <div className={css.skeletonLine} style={{ width: '50%', height: '14px', marginTop: '10px' }} />
  </div>
);

// Section component that's able to show article content
// The article content is mainly supposed to be inside a block
const SectionArticle = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    blocks,
    isInsideContainer,
    options,
  } = props;
  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasBlocks = blocks?.length > 0;
  const currentUser = options && options.currentUser;
  const isCurrentUserResolved = !!(currentUser && currentUser.id);

  const { accountType = 'company' } = (isCurrentUserResolved && currentUser?.attributes?.profile?.publicData) || {};
  // FractionalPros look for company/role listings; companies (and guests) look for fractional pros
  const expectedListingType = accountType === 'fractionalPro' ? 'company' : 'fractionalPro';
  const isDisplayingFractional = expectedListingType === 'fractionalPro';

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          infinite: true,
          dots: false
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          initialSlide: 2
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1
        }
      }
    ]
  };

  const shuffledRef = useRef(null);
  const listingsLengthRef = useRef(0);
  const listingsTypeRef = useRef(null);
  const listingsFirstIdRef = useRef(null);

  // Starts false so the first render always shows skeleton before loadData fires
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => { setHasMounted(true); }, []);

  const currentListingType = expectedListingType;
  const firstListingId = options?.listings?.[0]?.id?.uuid || null;

  if (
    options?.listings?.length &&
    (options.listings.length !== listingsLengthRef.current ||
      currentListingType !== listingsTypeRef.current ||
      firstListingId !== listingsFirstIdRef.current)
  ) {
    listingsLengthRef.current = options.listings.length;
    listingsTypeRef.current = currentListingType;
    listingsFirstIdRef.current = firstListingId;
    const shuffled = [...options.listings].sort(() => Math.random() - 0.5);
    const result = [];
    for (let i = 0; i < shuffled.length; i++) {
      const listing = shuffled[i];
      const currentExperience = listing?.attributes?.publicData?.experience;
      const lastExperience = result[result.length - 1]?.attributes?.publicData?.experience;
      if (currentExperience && currentExperience === lastExperience) continue;
      result.push(listing);
      if (result.length === LANDINGPAGE_SPOTLIGHT_NUMBER) break;
    }
    shuffledRef.current = result;
  }

  // Filter to only show listings of the expected type, preventing stale wrong-type listings from flashing
  const spotlightListings = (shuffledRef.current || []).filter(
    l => l?.attributes?.publicData?.listingType === expectedListingType
  );

  const isDailySpotlight = sectionId === "daily-talent-spotlight";
  const searchInProgress = options?.searchInProgress;
  const hasListings = spotlightListings.length > 0;

  // Show skeleton while:
  // 1. Not yet mounted (first render fires before loadData, so stale listings would flash)
  // 2. A search is in progress
  // 3. Listings exist but are the wrong type (user type changed, new search pending)
  const hasWrongTypeListings = !hasListings && options?.listings?.length > 0;
  const showSkeleton = isDailySpotlight && (!hasMounted || searchInProgress || hasWrongTypeListings);
  const showSection = !isDailySpotlight || showSkeleton || hasListings;

  return (showSection
    ? <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields ? (
        <header className={defaultClasses.sectionDetails}>
          <Field data={title} className={defaultClasses.title} options={fieldOptions} />
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
          <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
        </header>
      ) : null}
      {isDailySpotlight ? (
        <h1 className={css.spotlight}>
          {isDisplayingFractional ? 'Daily Fractional Spotlight' : 'Daily Role Spotlight'}
        </h1>
      ) : null}
      {isDailySpotlight && (
        showSkeleton
          ? <div className={css.talentSlider}>
              <div className={css.skeletonRow}>
                {Array.from({ length: 3 }).map((_, i) => (
                  <TalentCardSkeleton key={i} />
                ))}
              </div>
            </div>
          : <div className={css.talentSlider}>
              <Slider {...settings}>
                {spotlightListings?.map((listing, i) => (
                  <TalentCard
                    key={'listing_' + i}
                    listing={listing}
                    listingConfig={options.listingConfig}
                  />
                ))}
              </Slider>
            </div>
      )}
      {hasBlocks ? (
        <div
          className={classNames(defaultClasses.blockContainer, css.articleMain, {
            [css.noSidePaddings]: isInsideContainer,
          })}
        >
          <BlockBuilder
            blocks={blocks}
            ctaButtonClass={defaultClasses.ctaButton}
            options={options}
          />
        </div>
      ) : null}
    </SectionContainer>
    : null
  );
};

const propTypeOption = shape({
  fieldComponents: shape({ component: node, pickValidProps: func }),
});

SectionArticle.defaultProps = {
  className: null,
  rootClassName: null,
  defaultClasses: null,
  textClassName: null,
  title: null,
  description: null,
  appearance: null,
  callToAction: null,
  blocks: [],
  isInsideContainer: false,
  options: null,
};

SectionArticle.propTypes = {
  sectionId: string.isRequired,
  className: string,
  rootClassName: string,
  defaultClasses: shape({
    sectionDetails: string,
    title: string,
    description: string,
    ctaButton: string,
  }),
  title: object,
  description: object,
  appearance: object,
  callToAction: object,
  blocks: arrayOf(object),
  isInsideContainer: bool,
  options: propTypeOption,
};

export default SectionArticle;